import { placingPositionCorrection } from "../helper/placingPositionCorrection";
import { AddCommand } from "../models/commands/addCommand.model";
import { KonvaEvent } from "./konva-event-constants";
import { FeatureBase as FeatureBase } from "./feature-base";
import { Subject, takeUntil } from "rxjs";
import Konva from "konva";
import { EditorObject } from "../models/editor-object.model";
import { componentsContain } from "../helper/componentsContain";
import { ComponentNames } from "../helper/componentNames";
import { getComponentFromSource, getComponentsFromSource } from "../generics-helper/getComponentFromSource.generic";
import { LayerComponent } from "../models/components/commonComponents/LayerComponent";
import { EntranceDoorComponent } from "../models/components/objectComponents/EntranceDoorComponent";
import { IdentificationComponent } from "../models/components/commonComponents/IdentificationComponent";
const selectionRectName = 'selection-rect';

export class DefaultFeature extends FeatureBase {
    private contextMenuHandler: any;
    private mouseDownHandler: any;
    private mouseMoveHandler: any;
    private mouseUpHandler: any;
    private keyDownHandler: any;
    private keyUphandler: any;
    private unsubscribe$: Subject<void> = new Subject<void>();
    private x1;
    private y1;
    private x2;
    private y2;
    private multiselectOn: boolean = false;

    initialize() {
        this.contextMenuHandler = this.onContextMenu.bind(this);
        this.mouseDownHandler = this.onMouseDown.bind(this);
        this.mouseMoveHandler = this.onMouseMove.bind(this);
        this.mouseUpHandler = this.onMouseUp.bind(this);

        this.editorCanvasComponent.stage.on(KonvaEvent.contextmenu, this.contextMenuHandler);
        this.editorCanvasComponent.stage.on(KonvaEvent.mousedown, this.mouseDownHandler);
        this.editorCanvasComponent.stage.on(KonvaEvent.mousemove, this.mouseMoveHandler);
        this.editorCanvasComponent.stage.on(KonvaEvent.mouseup, this.mouseUpHandler);

        this.editorCanvasComponent.editorService.currentLayer.pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
            if (!value) {
                return;
            }
            this.addSelectionRectangle();
        });

        this.editorCanvasComponent.editorService.currentFloor.pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
            if (!value) {
                return;
            }
            this.addSelectionRectangle();
        });

        this.addContainerEvents();
    }

    dispose() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();

        this.removeContainerEvents();

        this.editorCanvasComponent.stage.off(KonvaEvent.contextmenu, this.contextMenuHandler);
        this.editorCanvasComponent.stage.off(KonvaEvent.mousedown, this.mouseDownHandler);
        this.editorCanvasComponent.stage.off(KonvaEvent.mousemove, this.mouseMoveHandler);
        this.editorCanvasComponent.stage.off(KonvaEvent.mouseup, this.mouseUpHandler);

        this.editorCanvasComponent.removeSelectionFromAllObjects();
    }

    onContextMenu(e) {
        e.evt.preventDefault();
    }

    onMouseDown(event) {
        // button 2 is the right mouse button, which is used for object rotation
        // therefore we early exit here in this feature
        if (event.evt.button === 2) {
            return;
        }

        // if a toolbox item is selected we just place it on the canvas and omit other mosudown related functionality
        if (this.editorCanvasComponent.editorService.isNewObj) {
            this.placeNewObject();
        }
        else {
            // mosue events which require the ctrl key are also not handled here as for now.
            // currently the ctrl key in combination with click is used, to select 1-n single objects in a group
            if (!event.evt.ctrlKey) {
                // if we have a instance of a editor object which is already selected we also exit early
                // single select/deselect should be handled in the object click handler outside this feature
                if (event.target instanceof EditorObject && (event.target as EditorObject).isSelected) {
                    return;
                }

                // deselect everything if the stage was clicked and there is no editor object underneath
                this.editorCanvasComponent.removeSelectionFromAllObjects();
                this.editorCanvasComponent.editorService.resetCurrentEditorObjectFromCanvas();

                this.multiselectOn = true;
                var selectionRectangle = this.getSelectionRectangle();

                var currentLayer = this.getCurrentLayer();

                var pos = this.getRelativePointerPosition(currentLayer);

                this.x1 = pos.x;
                this.y1 = pos.y;
                this.x2 = this.x1;
                this.y2 = this.y1;

                selectionRectangle.visible(true);
                selectionRectangle.width(0);
                selectionRectangle.height(0);
                selectionRectangle.position({ x: this.x1, y: this.y1 });

                currentLayer.batchDraw();
            }
        }
    }

    onMouseMove(e) {
        if (!this.multiselectOn) {
            return;
        }

        var selectionRectangle = this.getSelectionRectangle();

        var currentLayer = this.getCurrentLayer();

        var pos = this.getRelativePointerPosition(currentLayer);

        this.x2 = pos.x;
        this.y2 = pos.y;

        selectionRectangle.setAttrs({
            x: Math.min(this.x1, this.x2),
            y: Math.min(this.y1, this.y2),
            width: Math.abs(this.x2 - this.x1),
            height: Math.abs(this.y2 - this.y1),
        });

        currentLayer.batchDraw();
    }

    onMouseUp(event) {
        // button 2 is the right mouse button, which is used for object rotation
        // therefore we early exit here in this feature
        if (event.evt.button === 2) {
            return;
        }

        if (!this.multiselectOn) {
            return;
        }

        var selectionRectangle = this.getSelectionRectangle();
        selectionRectangle.visible(false);
        this.multiselectOn = false;

        if (selectionRectangle.width() === 0 && selectionRectangle.height() === 0) {
            return;
        }

        var currentLayerValue = this.editorCanvasComponent.editorService.currentLayer.value;

        var shapes = this.editorCanvasComponent.stage.find('Image').filter((object: EditorObject) => {
            var clickable = !componentsContain(object.components, ComponentNames.notClickableComponent);
            var layerComponents = getComponentsFromSource<LayerComponent>(ComponentNames.layerComponent, object.components);
            let isInCurrentLayer = false;
            layerComponents.forEach(x => {
                if (x.id === currentLayerValue) {
                    isInCurrentLayer = true;
                }
            });

            return clickable && isInCurrentLayer;
        });

        var box = selectionRectangle.getClientRect();

        var selectedObjects = [];
        shapes.forEach((shape) => {
            if (Konva.Util.haveIntersection(shape.getClientRect(), box)) {
                selectedObjects.push(shape);
            }
        });

        this.editorCanvasComponent.selectMultipleObjects(selectedObjects);
    }

    placeNewObject() {
        var currentObject = this.editorCanvasComponent.editorService.getCurrentEditorObject();
        var currentLayer = this.getCurrentLayer();

        var pos = this.getRelativePointerPosition(currentLayer);
        var posCorrected = placingPositionCorrection(pos, currentObject);

        //create add command and add it to service
        this.editorCanvasComponent.editorService.setCommand(new AddCommand(currentObject, posCorrected.x, posCorrected.y));
    }

    private addSelectionRectangle(){
        var selectionRectangle = new Konva.Rect({
            fill: 'rgba(250, 176, 2,0.75)',
            visible: false,
            name: selectionRectName
        });

        var currentLayer = this.getCurrentLayer();
        var previousSelectionRect = this.getSelectionRectangle();

        if (previousSelectionRect) {
            previousSelectionRect.destroy();
        }

        currentLayer.add(selectionRectangle);
    }

    private getSelectionRectangle() {
        return this.editorCanvasComponent.stage.find('.' + selectionRectName)[0];
    }

    private addContainerEvents() {
        const container = this.editorCanvasComponent.stage.container();
        container.tabIndex = 1;
        container.focus();

        this.keyUphandler = this.handleKeyUpBindings.bind(this);
        this.keyDownHandler = this.handleKeyDownBindings.bind(this);

        container.addEventListener('keydown', this.keyDownHandler);
        container.addEventListener('keyup', this.keyUphandler);
    }

    private removeContainerEvents() {
        const container = this.editorCanvasComponent.stage.container();

        container.removeEventListener('keydown', this.keyDownHandler);
        container.removeEventListener('keyup', this.keyUphandler);
    }

    private handleKeyDownBindings(e: KeyboardEvent) {
        this.editorCanvasComponent.debugLog('handleKeyBindings', new Date().toLocaleString());
        let ctrlKey = e.ctrlKey;
        let key = e.keyCode;

        if (ctrlKey) {
            // copy obj
            // 67 is C key
            if (key == 67 && this.editorCanvasComponent.selectedEditorObjects.children.length != 0) {
                this.copyObjects();
            }
        }
        else {
            // 46 is del key
            if (key == 46) {
                while (this.editorCanvasComponent.selectedEditorObjects.children.length > 0) {
                    const element = this.editorCanvasComponent.selectedEditorObjects.children[0];
                    this.editorCanvasComponent.editorService.removeObjectFromVariant((element as EditorObject)._id);
                    element.remove();
                }
                this.editorCanvasComponent.initSelectedItems();
            }

            // 32 is spacebar
            if (key == 32) {
                e.preventDefault();
                let snapingMode = 1;
                this.editorCanvasComponent.editorService.setCurrentSnapingMode(snapingMode);
                this.editorCanvasComponent.setSnapingMode(snapingMode);
            }
            if (key == 68) {
                // this.editorService.debugLog();
            }
        }
    }

    private handleKeyUpBindings(e: KeyboardEvent) {
        this.editorCanvasComponent.debugLog('handleKeyUpBindings');
        let key = e.keyCode;
        // 32 is spacebar
        if (key == 32) {
            e.preventDefault();
            // 0 is no snapping mode
            let snapingMode = 0;
            // emit to service
            this.editorCanvasComponent.editorService.setCurrentSnapingMode(snapingMode);
            // set internal state
            this.editorCanvasComponent.setSnapingMode(snapingMode);
            // destroy guidelines
            this.editorCanvasComponent.destroyGuidelines();
        }
    }

    private copyObjects() {
        this.editorCanvasComponent.debugLog('copyObjects ', new Date().toLocaleString());
        const positionOffsetForCopy = { x: 50, y: 50 };
        let copiedObjects = [];
        this.editorCanvasComponent.selectedEditorObjects.children.forEach((ele:EditorObject) => {
            let pos = ele.position();
            let absPos = ele.getAbsolutePosition();
            pos.x += positionOffsetForCopy.x;
            pos.y += positionOffsetForCopy.y;
            let copy = ele.copy();
            let entranceDoor = getComponentFromSource<EntranceDoorComponent>(ComponentNames.entranceDoorComponent, copy.components);
            if (entranceDoor != null) {
                let uniqueIdentifier = getComponentFromSource<IdentificationComponent>(ComponentNames.identificationComponent, copy.components);
                uniqueIdentifier.userId = this.editorCanvasComponent.editorService.getDefaultDoorName();
            }
            copy.setAbsolutePosition(pos);
            copiedObjects.push(copy);
        });

        this.editorCanvasComponent.initSelectedItems();

        this.editorCanvasComponent.debugLog('count of objects to be added again: ', copiedObjects.length);
        copiedObjects.forEach(ele => {
            let object = ele;
            let objectPos = object.position();
            this.editorCanvasComponent.editorService.setCommand(new AddCommand(object, objectPos.x, objectPos.y));
        });
    }
}
